import type { PlayerStatLine, TeamStatSummary } from '../types'

/**
 * The team-level figures on a game box score, in display order.
 *
 * @remarks
 * The game page and the upload preview both render from this list, so a new
 * total is one entry here rather than a second JSX grid. Values that come off
 * the roster (yards, sacks, interceptions thrown) are summed from player
 * lines. Values the ROM writes as team counters (first downs, TOP,
 * fourth-down stops credited to the defense, fumbles, FG blocks) are read off
 * {@link TeamStatSummary}.
 *
 * Extra counters the v5 ROM added — TOP, tackles, 4th-down stops, fumbles, FG
 * blocks — only exist from {@link EXTRA_STATS_FROM_SEASON} on. Earlier seasons
 * keep their stored box scores; those cells render as an em dash rather than a
 * fake zero. Injuries stay on the stored summary but are not shown: the v5
 * card puts blocked field goals in that slot.
 */

/** First season whose ROM writes the extra team counters onto the save. */
export const EXTRA_STATS_FROM_SEASON = 34

/** Shown in a cell whose ROM never recorded the figure. */
export const UNOWNED_STAT = '—'

/** Sums a stat across a team's roster, skipping slots that lack the category. */
export function sumTeamStat(
  players: PlayerStatLine[] | undefined,
  pick: (player: PlayerStatLine) => number
): number {
  return (players ?? []).reduce((sum, player) => sum + pick(player), 0)
}

/** `M:SS`, matching the post-game card. Zero seconds still prints `0:00`. */
export function formatPossession(seconds: number | undefined): string {
  const safe = Math.max(0, Math.floor(seconds ?? 0))
  const minutes = Math.floor(safe / 60)
  const remainder = safe % 60
  return `${minutes}:${remainder.toString().padStart(2, '0')}`
}

export interface TeamBoxField {
  /** Stable key; also the React list key. */
  key: string
  label: string
  value: (team: TeamStatSummary) => string | number
  /**
   * Season the ROM started recording this figure. Below that, the cell is an
   * em dash so a missing extra is not mistaken for a zero.
   */
  sinceSeason?: number
}

export const TEAM_BOX_FIELDS: TeamBoxField[] = [
  {
    key: 'offensiveYards',
    label: 'Offensive yards',
    value: team =>
      sumTeamStat(team.players, player => player.passing?.yards ?? 0) +
      sumTeamStat(team.players, player => player.rushing?.yards ?? 0),
  },
  {
    key: 'passing',
    label: 'Passing',
    value: team => sumTeamStat(team.players, player => player.passing?.yards ?? 0),
  },
  {
    key: 'rushing',
    label: 'Rushing',
    value: team => sumTeamStat(team.players, player => player.rushing?.yards ?? 0),
  },
  {
    key: 'specialTeamsYards',
    label: 'Special teams yards',
    value: team =>
      sumTeamStat(team.players, player => player.returns?.kickReturnYards ?? 0) +
      sumTeamStat(team.players, player => player.returns?.puntReturnYards ?? 0),
  },
  {
    key: 'firstDowns',
    label: '1st downs',
    value: team => team.firstDowns ?? 0,
  },
  {
    key: 'timeOfPossession',
    label: 'TOP',
    value: team => formatPossession(team.timeOfPossessionSeconds),
    sinceSeason: EXTRA_STATS_FROM_SEASON,
  },
  {
    key: 'tackles',
    label: 'Tackles',
    value: team => sumTeamStat(team.players, player => player.defense?.tackles ?? 0),
    sinceSeason: EXTRA_STATS_FROM_SEASON,
  },
  {
    key: 'sacks',
    label: 'Sacks',
    value: team => sumTeamStat(team.players, player => player.defense?.sacks ?? 0),
  },
  {
    key: 'fourthDownStops',
    label: '4th down stops',
    value: team => team.fourthDownStops ?? 0,
    sinceSeason: EXTRA_STATS_FROM_SEASON,
  },
  {
    key: 'intThrown',
    label: 'INT thrown',
    value: team => sumTeamStat(team.players, player => player.passing?.interceptions ?? 0),
  },
  {
    key: 'fumbles',
    label: 'Lost fum/total',
    value: team => `${team.fumblesLost ?? 0}/${team.fumblesTotal ?? 0}`,
    sinceSeason: EXTRA_STATS_FROM_SEASON,
  },
  {
    key: 'fieldGoalBlocks',
    label: 'FG blocks',
    value: team => team.fieldGoalBlocks ?? 0,
    sinceSeason: EXTRA_STATS_FROM_SEASON,
  },
]
